import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

/**
 * Email Service - Wrapper for Firebase Cloud Functions email functionality
 */

// (Removed) sendPortalInvite / sendPortalInviteExisting — the temp-password
// invite wrappers, retired 2026-07-15. Portal access now goes out through the
// createPortalClaim callable (claim-link flow, §3g), called directly from
// SendPortalInvite.js.

/**
 * Send lesson notes after a lesson
 * @param {Object} params - Email parameters
 * @param {string} params.studentName - Full name of student
 * @param {string} params.parentEmail - Parent email address
 * @param {string} params.lessonDate - Date of lesson (ISO string)
 * @param {string} params.notes - Lesson notes/summary
 * @param {string} params.nextLessonDate - Next lesson date (optional)
 * @param {string} params.assignedMaterials - Materials assigned (optional, HTML string)
 * @param {Array} params.attachments - File attachments (optional)
 */
export const sendLessonNotes = async (params) => {
  try {
    const sendEmail = httpsCallable(functions, 'sendLessonNotes');
    const result = await sendEmail(params);
    return result.data;
  } catch (error) {
    console.error('Error sending lesson notes:', error);
    throw error;
  }
};

/**
 * Send lesson reminder
 * @param {Object} params - Email parameters
 * @param {string} params.studentName - Full name of student
 * @param {string} params.parentEmail - Parent email address
 * @param {string} params.lessonDate - Date of lesson (ISO string)
 * @param {string} params.lessonTime - Time of lesson (e.g., "2:00 PM")
 * @param {string} params.location - Lesson location (optional)
 */
export const sendLessonReminder = async (params) => {
  try {
    const sendEmail = httpsCallable(functions, 'sendLessonReminder');
    const result = await sendEmail(params);
    return result.data;
  } catch (error) {
    console.error('Error sending lesson reminder:', error);
    throw error;
  }
};

/**
 * Send payment receipt
 * @param {Object} params - Email parameters
 * @param {string} params.studentName - Full name of student
 * @param {string} params.parentEmail - Parent email address
 * @param {number} params.amount - Payment amount
 * @param {string} params.paymentDate - Payment date (ISO string)
 * @param {string} params.paymentMethod - Payment method (e.g., "Credit Card", "Cash")
 * @param {string} params.invoiceNumber - Invoice number
 * @param {number} params.balance - Remaining balance (optional)
 */
export const sendPaymentReceipt = async (params) => {
  try {
    const sendEmail = httpsCallable(functions, 'sendPaymentReceipt');
    const result = await sendEmail(params);
    return result.data;
  } catch (error) {
    console.error('Error sending payment receipt:', error);
    throw error;
  }
};

/**
 * Send announcement to multiple recipients
 * @param {Object} params - Email parameters
 * @param {string} params.title - Announcement title/subject
 * @param {string} params.message - Announcement message
 * @param {Array} params.recipients - Array of {name, email} objects
 */
export const sendAnnouncement = async (params) => {
  try {
    const sendEmail = httpsCallable(functions, 'sendAnnouncement');
    const result = await sendEmail(params);
    return result.data;
  } catch (error) {
    console.error('Error sending announcement:', error);
    throw error;
  }
};

/**
 * Send a free-form portal email to one or more families/students (E4 Layer 1).
 * Gated server-side on the sendEmail permission (admins/owners bypass).
 * @param {Object} params
 * @param {Array}  params.recipients - Array of {name?, email} objects
 * @param {string} params.subject    - Email subject (also the heading)
 * @param {string} params.bodyHtml   - Pre-formatted inner HTML body
 * @param {string} [params.studentId] - Optional student context for the log
 * @param {string} [params.familyId]  - Optional family context for the log
 */
export const sendPortalEmail = async (params) => {
  try {
    const send = httpsCallable(functions, 'sendPortalEmail');
    const result = await send(params);
    return result.data;
  } catch (error) {
    console.error('Error sending portal email:', error);
    throw error;
  }
};

/**
 * Send password reset email
 * @param {Object} params - Email parameters
 * @param {string} params.recipientName - Recipient name
 * @param {string} params.recipientEmail - Recipient email
 * @param {string} params.resetLink - Password reset link
 */
export const sendPasswordReset = async (params) => {
  try {
    const sendEmail = httpsCallable(functions, 'sendPasswordReset');
    const result = await sendEmail(params);
    return result.data;
  } catch (error) {
    console.error('Error sending password reset:', error);
    throw error;
  }
};

/**
 * Helper function to send batch emails to multiple students
 * Useful for sending announcements or reminders to a group
 */
export const sendBatchEmails = async (emailFunction, students, getEmailParams) => {
  const results = {
    success: [],
    failed: [],
  };

  for (const student of students) {
    try {
      const params = getEmailParams(student);
      await emailFunction(params);
      results.success.push(student);
    } catch (error) {
      console.error(`Failed to send email to ${student.email}:`, error);
      results.failed.push({ student, error: error.message });
    }
  }

  return results;
};

export default {
  sendLessonNotes,
  sendLessonReminder,
  sendPaymentReceipt,
  sendAnnouncement,
  sendPortalEmail,
  sendPasswordReset,
  sendBatchEmails,
};
